import React from "react";
import axios from "axios";
import { Phone, Mail, MapPin, MessageCircle, Send, CheckCircle2, ExternalLink, Sparkles, Check, MessageSquare, Wrench } from "lucide-react";
import { CONTACT, MESSENGER_URL, AIRBNB_INTRO, AIRBNB_LISTINGS, ADMIN_CONTACTS, CARETAKER_CONTACTS } from "@/data/staycation";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = React.useState({ state: "idle", message: "" });

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus({ state: "error", message: "Please fill in your name, email and message." });
      return;
    }
    setStatus({ state: "loading", message: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({ state: "success", message: "Thanks! We'll get back to you soon. For faster replies, message us on Messenger." });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setStatus({ state: "error", message: err?.response?.data?.detail || "Something went wrong. Please try again or message us on Messenger." });
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo
        title="Contact — Book on Messenger, call/text admin & caretakers"
        description="Get in touch with Xanders Staycations: chat on Messenger for instant booking, call or text our 4 admin numbers or 4 on-site caretakers, or email xanderstaycation2026@gmail.com."
        path="/contact"
      />
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pt-14 pb-10">
        <p className="xs-eyebrow">Get in touch</p>
        <h1 className="font-display text-4xl sm:text-5xl font-black mt-2 max-w-2xl">Let's plan your next cozy escape.</h1>
        <p className="mt-4 text-neutral-600 max-w-xl">Send us a message — we'll reply within a few hours. For instant availability checks, hit us up on Messenger.</p>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-24 grid lg:grid-cols-5 gap-8">
        <aside className="lg:col-span-2 space-y-4">
          <a
            href={MESSENGER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-messenger-card"
            className="block rounded-[1.25rem] p-6 bg-[var(--xs-primary)] text-white border border-transparent transition-colors hover:bg-[var(--xs-primary-hover)]"
          >
            <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-white/20">
              <MessageCircle className="h-5 w-5" />
            </div>
            <h3 className="font-display text-xl font-bold mt-4">Chat on Messenger</h3>
            <p className="mt-2 text-sm text-white/90">Fastest way to confirm availability and book your stay.</p>
            <p className="mt-3 text-sm font-semibold">m.me/558751367325531 →</p>
          </a>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="xs-card p-5" data-testid="admin-contacts">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[var(--xs-primary-soft)]">
                <Phone className="h-4 w-4 text-[var(--xs-primary)]" />
              </div>
              <h3 className="font-display text-base font-bold mt-3">Admin — call or text</h3>
              <ul className="mt-3 space-y-2.5">
                {ADMIN_CONTACTS.map((c) => {
                  const tel = c.phone.replace(/\s/g, "");
                  return (
                    <li key={c.name} className="flex items-center justify-between gap-2" data-testid={`admin-${c.name.toLowerCase()}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-neutral-900 truncate">{c.name}</p>
                        <p className="text-xs text-neutral-600 truncate">{c.phone}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {c.actions.includes("call") && (
                          <a href={`tel:${tel}`} aria-label={`Call ${c.name}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 hover:border-[var(--xs-primary)] hover:text-[var(--xs-primary)] text-neutral-700">
                            <Phone className="h-3.5 w-3.5" />
                          </a>
                        )}
                        {c.actions.includes("sms") && (
                          <a href={`sms:${tel}`} aria-label={`Text ${c.name}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 hover:border-[var(--xs-primary)] hover:text-[var(--xs-primary)] text-neutral-700">
                            <MessageSquare className="h-3.5 w-3.5" />
                          </a>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="xs-card p-5" data-testid="caretaker-contacts">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[var(--xs-primary-soft)]">
                <Wrench className="h-4 w-4 text-[var(--xs-primary)]" />
              </div>
              <h3 className="font-display text-base font-bold mt-3">Caretakers</h3>
              <p className="text-[11px] text-neutral-500 mt-0.5">Unit-specific concerns</p>
              <ul className="mt-3 space-y-2.5">
                {CARETAKER_CONTACTS.map((c) => {
                  const tel = c.phone.replace(/\s/g, "");
                  return (
                    <li key={c.name} className="flex items-center justify-between gap-2" data-testid={`caretaker-${c.name.toLowerCase()}`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-neutral-900 truncate">{c.name}</p>
                        <p className="text-[11px] text-[var(--xs-primary)] font-semibold truncate">{c.area}</p>
                        <p className="text-xs text-neutral-600 truncate">{c.phone}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <a href={`tel:${tel}`} aria-label={`Call ${c.name}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 hover:border-[var(--xs-primary)] hover:text-[var(--xs-primary)] text-neutral-700">
                          <Phone className="h-3.5 w-3.5" />
                        </a>
                        <a href={`sms:${tel}`} aria-label={`Text ${c.name}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 hover:border-[var(--xs-primary)] hover:text-[var(--xs-primary)] text-neutral-700">
                          <MessageSquare className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="xs-card p-5">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[var(--xs-primary-soft)]">
                <Mail className="h-4 w-4 text-[var(--xs-primary)]" />
              </div>
              <h3 className="font-display text-base font-bold mt-3">Email</h3>
              <a href={`mailto:${CONTACT.email}`} className="mt-1 text-xs text-neutral-700 hover:text-[var(--xs-primary)] break-all inline-block" data-testid="contact-email">{CONTACT.email}</a>
            </div>

            <div className="xs-card p-5">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-[var(--xs-primary-soft)]">
                <MapPin className="h-4 w-4 text-[var(--xs-primary)]" />
              </div>
              <h3 className="font-display text-base font-bold mt-3">Location</h3>
              <p className="mt-1 text-xs text-neutral-700">{CONTACT.address}</p>
            </div>
          </div>
        </aside>

        <form onSubmit={submit} className="lg:col-span-3 xs-card p-6 sm:p-8 hover:translate-y-0" data-testid="contact-form">
          <h3 className="font-display text-2xl font-bold">Send us a message</h3>
          <p className="text-sm text-neutral-500 mt-1">We typically reply within a few hours.</p>

          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <Field label="Your name" value={form.name} onChange={update("name")} testid="contact-name" required />
            <Field label="Email" type="email" value={form.email} onChange={update("email")} testid="contact-email-input" required />
            <Field label="Phone (optional)" value={form.phone} onChange={update("phone")} testid="contact-phone-input" />
            <Field label="Subject (optional)" value={form.subject} onChange={update("subject")} testid="contact-subject" />
          </div>
          <div className="mt-4">
            <label className="text-xs font-semibold text-neutral-600 uppercase tracking-wider">Message *</label>
            <textarea
              required
              rows={5}
              value={form.message}
              onChange={update("message")}
              data-testid="contact-message"
              className="mt-1.5 w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--xs-primary)] focus:border-transparent"
              placeholder="Tell us your dates, preferred location, and any questions…"
            />
          </div>

          {status.state === "error" && (
            <p className="mt-4 text-sm text-red-600" data-testid="contact-error">{status.message}</p>
          )}
          {status.state === "success" && (
            <p className="mt-4 text-sm text-emerald-700 inline-flex items-center gap-2" data-testid="contact-success">
              <CheckCircle2 className="h-4 w-4" /> {status.message}
            </p>
          )}

          <button
            type="submit"
            disabled={status.state === "loading"}
            data-testid="contact-submit"
            className="xs-btn-primary mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-60"
          >
            <Send className="h-4 w-4" /> {status.state === "loading" ? "Sending…" : "Send Message"}
          </button>
        </form>
      </section>

      {/* Airbnb listings */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-24" data-testid="airbnb-section">
        <div className="rounded-3xl bg-white border border-neutral-200 p-8 sm:p-12">
          <div className="grid lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-1">
              <p className="xs-eyebrow inline-flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> Also book on Airbnb</p>
              <h2 className="font-display text-3xl sm:text-4xl font-black mt-3 leading-[1.1]">{AIRBNB_INTRO.title}</h2>
              <p className="mt-2 text-sm font-semibold text-[var(--xs-primary)]">{AIRBNB_INTRO.subtitle}</p>
              <p className="mt-4 text-sm text-neutral-600 leading-relaxed">{AIRBNB_INTRO.description}</p>

              <p className="mt-6 text-[11px] uppercase tracking-widest font-semibold text-neutral-500">What we offer</p>
              <ul className="mt-3 space-y-1.5 text-sm">
                {AIRBNB_INTRO.offers.map((o) => (
                  <li key={o} className="flex items-start gap-2"><Check className="h-4 w-4 text-[var(--xs-primary)] mt-0.5 shrink-0" />{o}</li>
                ))}
              </ul>

              <p className="mt-6 text-[11px] uppercase tracking-widest font-semibold text-neutral-500">Locations</p>
              <ul className="mt-3 space-y-1.5 text-sm">
                {AIRBNB_INTRO.locations.map((l) => (
                  <li key={l} className="flex items-start gap-2"><MapPin className="h-4 w-4 text-[var(--xs-primary)] mt-0.5 shrink-0" />{l}</li>
                ))}
              </ul>

              <div className="mt-6 rounded-2xl bg-[var(--xs-bg-alt)] p-4 text-sm text-neutral-700">
                <p className="font-semibold text-neutral-900">How to book</p>
                <ol className="mt-2 space-y-1 list-decimal list-inside text-neutral-700">
                  <li>Click any Airbnb link to book directly.</li>
                  <li>PM us on <strong>Xander's Staycations</strong> FB page.</li>
                  <li>Or message us anytime — we'll guide you through.</li>
                </ol>
                <a href={MESSENGER_URL} target="_blank" rel="noopener noreferrer" className="xs-btn-primary mt-4 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold" data-testid="airbnb-messenger-cta">
                  <MessageCircle className="h-3.5 w-3.5" /> Ask via Messenger
                </a>
              </div>
            </div>

            <div className="lg:col-span-2">
              <p className="text-[11px] uppercase tracking-widest font-semibold text-neutral-500 mb-4">Available Airbnb Units (30)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {AIRBNB_LISTINGS.map((l) => (
                  <a
                    key={l.n}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`airbnb-listing-${l.n}`}
                    className="group rounded-2xl border border-neutral-200 hover:border-[var(--xs-primary)] bg-white p-4 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-[var(--xs-primary-soft)] text-[var(--xs-primary)] text-xs font-bold">{l.n}</span>
                      <ExternalLink className="h-4 w-4 text-neutral-400 group-hover:text-[var(--xs-primary)]" />
                    </div>
                    <p className="mt-3 font-display text-base font-bold leading-tight">Staycation {l.n} · {l.name}</p>
                    <p className="text-xs text-neutral-500 mt-0.5">{l.floor} · {l.type} · {l.view}</p>
                    {l.altUrl && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); window.open(l.altUrl, "_blank", "noopener,noreferrer"); }}
                        className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--xs-primary)] hover:underline"
                      >
                        Also available as {l.altLabel} <ExternalLink className="h-3 w-3" />
                      </button>
                    )}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-neutral-600 uppercase tracking-wider">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="mt-1.5 w-full rounded-full border border-neutral-200 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--xs-primary)] focus:border-transparent"
      />
    </div>
  );
}
